import React, { useState, useEffect } from "react";
import {
  DatePicker,
  Input,
  Select,
  Button,
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Tooltip,
  message,
} from "antd";
import { WarningOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useFetchErrorSummary } from "../../../../hooks/useCaseAudit";
import { useErrorTopicOptions } from "../../../../hooks/useErrorTopic";
import DetailsModal from "../../ReportAdmin/components/DetailsModal";

const { RangePicker } = DatePicker;

// ป้ายบอกว่าเคสนี้ถูกจับผิดจากช่องทางไหน
const SOURCE_META = {
  excel: { label: "Excel (Manual DEP)", color: "blue" },
  panel: { label: "Panel report", color: "purple" },
  admin: { label: "Admin ตรวจเอง", color: "orange" },
};

const SOURCE_OPTIONS = Object.keys(SOURCE_META).map((k) => ({
  value: k,
  label: SOURCE_META[k].label,
}));

// สถานะของความผิดพลาด — 'Edited' มีเฉพาะฝั่ง Excel (มีคนกดแก้เคสหลังตัดสินว่าผิด)
const STATUS_META = {
  Fail: { label: 'ผิด (ยังไม่แก้)', color: 'red' },
  Edited: { label: 'ผิด (แก้แล้ว)', color: 'gold' },
};

const STATUS_OPTIONS = Object.keys(STATUS_META).map((k) => ({
  value: k,
  label: STATUS_META[k].label,
}));

const ErrorSummaryPage = () => {
  const today = dayjs();
  const [selectedDate, setSelectedDate] = useState([today.subtract(30, "day"), today]);
  const [source, setSource] = useState("");
  const [errorStatus, setErrorStatus] = useState("");
  const [topic, setTopic] = useState("");
  const [userId, setUserId] = useState("");
  const [finishBy, setFinishBy] = useState("");
  const [summary, setSummary] = useState(null);
  const [detailInputId, setDetailInputId] = useState(null);

  const { mutateAsync: fetchSummary, isLoading } = useFetchErrorSummary();
  const { options: topicOptions } = useErrorTopicOptions();

  const runSearch = async (f) => {
    try {
      const payload = {
        startDate: f.selectedDate[0].format("YYYY-MM-DD"),
        endDate: f.selectedDate[1].format("YYYY-MM-DD"),
      };
      if (f.source) payload.source = f.source;
      if (f.errorStatus) payload.errorStatus = f.errorStatus;
      if (f.topic) payload.topic = f.topic;
      if (f.userId?.trim()) payload.userId = f.userId.trim();
      if (f.finishBy?.trim()) payload.finishBy = f.finishBy.trim();

      const res = await fetchSummary(payload);
      setSummary(res?.result ?? null);
    } catch (error) {
      await message.destroy();
      message.error("โหลดสรุปเคสผิดพลาดไม่สำเร็จ");
    }
  };

  const handleSearch = () =>
    runSearch({ selectedDate, source, errorStatus, topic, userId, finishBy });

  const handleClear = () => {
    const empty = {
      selectedDate: [today.subtract(30, "day"), today],
      source: "",
      errorStatus: "",
      topic: "",
      userId: "",
      finishBy: "",
    };
    setSelectedDate(empty.selectedDate);
    setSource(empty.source);
    setErrorStatus(empty.errorStatus);
    setTopic(empty.topic);
    setUserId(empty.userId);
    setFinishBy(empty.finishBy);
    // ค้นใหม่ด้วยค่าที่เพิ่งล้าง — อ่านจาก state ไม่ได้เพราะ setState ยังไม่มีผลรอบนี้
    runSearch(empty);
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: "วันที่ตรวจ",
      dataIndex: "audited_date",
      key: "audited_date",
      align: "center",
      width: 165,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "-"),
    },
    {
      title: "ช่องทาง",
      dataIndex: "source",
      key: "source",
      align: "center",
      width: 160,
      render: (v) => (
        <Tag color={SOURCE_META[v]?.color}>{SOURCE_META[v]?.label || v}</Tag>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "error_status",
      key: "error_status",
      align: "center",
      width: 130,
      render: (v) => (
        <Tag color={STATUS_META[v]?.color}>{STATUS_META[v]?.label || v}</Tag>
      ),
    },
    {
      title: "พนักงาน",
      dataIndex: "finish_by_name",
      key: "finish_by_name",
      align: "center",
      width: 130,
      render: (v) => v || "-",
    },
    {
      title: "หัวข้อที่ผิด",
      dataIndex: "topic",
      key: "topic",
      width: 170,
      render: (v) => v || <span style={{ color: "#bbb" }}>ไม่ระบุ</span>,
    },
    {
      title: "Case",
      dataIndex: "caseName",
      key: "caseName",
      align: "center",
      width: 110,
      render: (v) => v || "-",
    },
    { title: "User ID", dataIndex: "userId", key: "userId", align: "center", width: 130 },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 110,
      render: (v) => (v == null ? "-" : Number(v).toLocaleString("th-TH")),
    },
    {
      // ยอดความเสียหายจริงที่คนตรวจกรอกเอง — คนละตัวกับ Amount ของเคส
      title: "ยอดเสียหาย",
      dataIndex: "wrong_amount",
      key: "wrong_amount",
      align: "right",
      width: 120,
      render: (v) =>
        v == null ? (
          <span style={{ color: "#bbb" }}>ยังไม่ระบุ</span>
        ) : (
          <span style={{ color: "#cf1322", fontWeight: 600 }}>
            {Number(v).toLocaleString("th-TH")}
          </span>
        ),
    },
    {
      title: "รายละเอียด",
      dataIndex: "note",
      key: "note",
      render: (v, r) => (
        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {r.isDuplicated && (
            <Tooltip
              title={
                r.counted === false
                  ? "เคสนี้ถูกจับผิดหลายช่องทาง — แถวนี้ไม่ถูกนับเข้ายอด (นับจากช่องทางอื่นแล้ว)"
                  : "เคสนี้ถูกจับผิดหลายช่องทาง — นับจากแถวนี้"
              }
            >
              <WarningOutlined style={{ color: "#fa8c16", marginRight: 6 }} />
            </Tooltip>
          )}
          {/* แถวที่ไม่ถูกนับยังแสดงอยู่ (ประวัติการตรวจไม่หาย) แต่ทำให้จางลงให้เห็นต่าง */}
          <span style={r.counted === false ? { opacity: 0.45 } : undefined}>{v || "-"}</span>
          {r.counted === false && (
            <Tag style={{ marginLeft: 6 }}>ไม่นับซ้ำ</Tag>
          )}
        </div>
      ),
    },
    {
      title: "รายละเอียด",
      key: "detail",
      align: "center",
      width: 110,
      fixed: "right",
      render: (_, r) =>
        // รายการ Not Match ไม่มี input_id → ไม่มีเคสให้เปิดดู
        r.input_id == null ? (
          <Tooltip title="รายการนี้ไม่มีเคสในระบบ (เงินเข้าธนาคารแต่ไม่มีเคสรองรับ)">
            <span style={{ color: "#bbb" }}>ไม่มีเคส</span>
          </Tooltip>
        ) : (
          <Button
            type="text"
            icon={<SearchOutlined style={{ color: "#1890ff", fontSize: 18 }} />}
            onClick={() => setDetailInputId(r.input_id)}
          />
        ),
    },
  ];

  const labelStyle = {
    display: "block",
    marginBottom: 8,
    fontSize: 14,
    fontWeight: 500,
    color: "#333",
  };

  const dupCount = summary?.duplicatedInputIds?.length || 0;

  return (
    <div style={{ padding: 16, background: "#f5f5f5", minHeight: "100vh", marginLeft: 16 }}>
      <h1 style={{ margin: "0 0 20px", fontSize: "2rem", fontWeight: "bold", color: "#212121" }}>
        สรุปเคสผิดพลาดทั้งหมด
      </h1>

      {/* ตัวกรอง */}
      <div style={{ background: "#fff", borderRadius: 10, padding: 20, marginBottom: 20, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 16, alignItems: "end" }}>
          <div>
            <label style={labelStyle}>วันที่ตรวจ</label>
            <RangePicker
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY"
              style={{ width: "100%" }}
              allowClear={false}
            />
          </div>
          <div>
            <label style={labelStyle}>ช่องทางที่ตรวจพบ</label>
            <Select
              value={source || undefined}
              onChange={(v) => setSource(v ?? "")}
              options={SOURCE_OPTIONS}
              placeholder="ทั้งหมด"
              allowClear
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label style={labelStyle}>สถานะ</label>
            <Select
              value={errorStatus || undefined}
              onChange={(v) => setErrorStatus(v ?? "")}
              options={STATUS_OPTIONS}
              placeholder="ทั้งหมด"
              allowClear
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label style={labelStyle}>หัวข้อที่ผิด</label>
            <Select
              value={topic || undefined}
              onChange={(v) => setTopic(v ?? "")}
              options={topicOptions}
              placeholder="ทั้งหมด"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <label style={labelStyle}>User ID</label>
            <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="ทั้งหมด" />
          </div>
          <div>
            <label style={labelStyle}>พนักงาน (คนปิดเคส)</label>
            <Input value={finishBy} onChange={(e) => setFinishBy(e.target.value)} placeholder="ทั้งหมด" />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button type="primary" onClick={handleSearch} loading={isLoading}>
              ค้นหา
            </Button>
            <Button onClick={handleClear}>ล้างค่า</Button>
          </div>
        </div>
      </div>

      {/* ยอดรวม */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="ความผิดพลาดทั้งหมด" value={summary?.total || 0} suffix="ครั้ง" />
            {/* total หักซ้ำแล้ว — บอกให้เห็นว่าตัดออกไปกี่แถว จะได้ไม่งงว่าทำไมไม่ตรงกับจำนวนแถวในตาราง */}
            {summary?.totalRows > summary?.total && (
              <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
                จาก {summary.totalRows} แถว · ตัดที่นับซ้ำออก{" "}
                {summary.totalRows - summary.total}
              </div>
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="จำนวนเคส (ไม่นับซ้ำ)"
              value={summary?.uniqueCases || 0}
              suffix="เคส"
              valueStyle={{ color: dupCount > 0 ? "#fa8c16" : undefined }}
            />
            {dupCount > 0 && (
              <div style={{ fontSize: 12, color: "#fa8c16", marginTop: 4 }}>
                มี {dupCount} เคสถูกจับซ้ำหลายช่องทาง (นับให้แล้วเคสละครั้ง)
              </div>
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="ยอดความเสียหายรวม"
              value={summary?.totalWrongAmount || 0}
              precision={2}
              valueStyle={{ color: "#cf1322" }}
            />
            {summary?.unspecifiedAmountCount > 0 && (
              <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 4 }}>
                ยังไม่ระบุยอด {summary.unspecifiedAmountCount} รายการ
              </div>
            )}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Panel report / Admin"
              value={(summary?.bySource?.panel || 0) + (summary?.bySource?.admin || 0)}
            />
            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
              ในจำนวนนี้ แก้แล้ว {summary?.byStatus?.Edited || 0} รายการ
            </div>
          </Card>
        </Col>
      </Row>

      {/* แยกตามหัวข้อ / พนักงาน */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} lg={12}>
          <Card title="แยกตามหัวข้อที่ผิด" size="small">
            <Table
              rowKey="topic"
              size="small"
              dataSource={summary?.byTopic || []}
              pagination={false}
              columns={[
                { title: "หัวข้อ", dataIndex: "topic", key: "topic" },
                { title: "จำนวน", dataIndex: "count", key: "count", align: "right", width: 90 },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="แยกตามพนักงาน" size="small">
            <Table
              rowKey="name"
              size="small"
              dataSource={summary?.byEmployee || []}
              pagination={false}
              columns={[
                { title: "พนักงาน", dataIndex: "name", key: "name" },
                { title: "จำนวน", dataIndex: "count", key: "count", align: "right", width: 90 },
                {
                  title: "ยอดเสียหาย",
                  dataIndex: "amount",
                  key: "amount",
                  align: "right",
                  width: 140,
                  render: (v, r) => (
                    <>
                      <div>{Number(v || 0).toLocaleString("th-TH")}</div>
                      {r.unspecified > 0 && (
                        <div style={{ fontSize: 11, color: "#bbb" }}>
                          ยังไม่ระบุ {r.unspecified}
                        </div>
                      )}
                    </>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* รายการทั้งหมด */}
      <div style={{ background: "#fff", borderRadius: 10, padding: 20, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
        <Table
          rowKey={(r) => `${r.source}-${r.source_id}`}
          loading={isLoading}
          dataSource={summary?.rows || []}
          columns={columns}
          scroll={{ x: "max-content" }}
          bordered
          size="small"
          pagination={{ position: ["topRight"], showSizeChanger: true }}
        />
      </div>

      {/* ใช้ modal ตัวเดียวกับหน้า Report (Panel) — isDetail ปิดปุ่ม Pass/Fail ให้เป็นแบบดูอย่างเดียว */}
      <DetailsModal
        visible={detailInputId != null}
        onClose={() => setDetailInputId(null)}
        inputId={detailInputId}
        isDetail
      />
    </div>
  );
};

export default ErrorSummaryPage;
